/**
 * "Tell the Build Team" dialog — in-app replacement for the mailto: links that opened
 * Outlook on Tim's machine (Beta-1 feedback D08 "Talk to Humans" notes).
 *
 * Opened from anywhere via openFeedback(type) (src/config/feedback.js). Writes a
 * Hugo_Feedback document with page/org/role context; n8n emails the build team (P7-10).
 * The email fallback stays one click away in case the write fails or someone prefers it.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Chip,
  IconButton,
  Alert,
  Link,
  CircularProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDemoRole } from '../../contexts/DemoRoleContext';
import { useUserOrganizations } from '../../assistants/drew/hooks/useOrganization';
import { submitFeedback } from '../../services/feedbackService';
import { restoreAlertsForUser } from '../../services/documentService';
import {
  FEEDBACK_TYPES,
  FEEDBACK_EMAIL,
  OPEN_FEEDBACK_EVENT,
  feedbackMailto,
} from '../../config/feedback';

const PLACEHOLDERS = {
  feedback: 'What would make REPSit.ai more useful? Anything goes — a wording nit, a workflow, a wish.',
  bug: 'What did you do, what did you expect, and what happened instead? Screenshots can be emailed after.',
  confusing: 'Which screen or wording was unclear, and what did you expect it to mean?',
  question: 'Ask away — a human answers, usually fast.',
  sandbox_reset: 'Optional: tell us what you were doing before it broke. We will restore Magnum, Inc. to its starting point.',
  api_interest: 'What would you connect first (CMMS, LMS, incident system…) and which data do you need out of REPSit.ai?',
};

// Tim (1 Nov): the tester chooses how far back a reset goes.
const RESET_SCOPES = {
  data: { label: 'Data only', hint: 'OE Conditions, surveys and responses, scores and alerts go back to the seeded 90 days. Members, settings and documents you added stay.' },
  full: { label: 'Everything — back to day one', hint: 'Also removes members, settings and documents you added. The whole sandbox is rebuilt as you first received it.' },
};

export default function FeedbackDialog() {
  const { user } = useAuth();
  const { personaId, viewAsUid, persona } = useDemoRole();
  const { organizations } = useUserOrganizations(user?.uid);
  const location = useLocation();
  const organization = organizations?.[0] || null;

  const [open, setOpen] = useState(false);
  const [type, setType] = useState('feedback');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sentId, setSentId] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setType(e.detail?.type || 'feedback');
      setMessage(e.detail?.message || '');
      setError(null);
      setSentId(null);
      setOpen(true);
    };
    window.addEventListener(OPEN_FEEDBACK_EVENT, handler);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, handler);
  }, []);

  const close = () => { if (!busy) setOpen(false); };

  const handleSend = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await submitFeedback({
        type,
        message: type === 'sandbox_reset'
          ? `[Reset scope: ${RESET_SCOPES[resetScope].label}] ${message.trim() || 'Please reset my sandbox.'}`
          : message,
        user,
        organization,
        personaId,
        viewAsUid,
      });
      setSentId(res.id);
    } catch (err) {
      setError(err?.message || 'Could not send. Please use the email link below.');
    } finally {
      setBusy(false);
    }
  };

  // P3-06: acknowledgement is per user; a tester can bring their own dismissed alerts back.
  const [copiedAddr, setCopiedAddr] = useState(false);
  const [resetScope, setResetScope] = useState('data');
  const [restored, setRestored] = useState(null);
  useEffect(() => setRestored(null), [open, type]);
  const handleRestoreAlerts = async () => {
    try {
      setRestored(await restoreAlertsForUser(organization?.id, user?.uid));
    } catch (err) {
      console.error('Restore demo alerts failed:', err);
      setRestored(0);
    }
  };

  const contextChips = [
    location?.pathname && { label: location.pathname },
    organization?.name && { label: organization.name },
    persona?.label && { label: viewAsUid ? `Viewing as member (${persona.label})` : persona.label },
  ].filter(Boolean);

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Tell the Build Team
        <IconButton size="small" onClick={close} disabled={busy} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {sentId ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Sent — thank you.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Alex and Tim read every note. {type === 'sandbox_reset'
                ? 'Your Magnum, Inc. sandbox will be restored within a few minutes.'
                : 'You will hear back by email if a reply is needed.'}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              There is no support queue — just the two people building this. Bugs, confusing screens,
              ideas, or a sandbox reset: this goes straight to them.
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              label="What is this about?"
              value={type}
              onChange={(e) => setType(e.target.value)}
              sx={{ mb: 2 }}
            >
              {FEEDBACK_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
            {type === 'sandbox_reset' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>How far back?</Typography>
                <RadioGroup value={resetScope} onChange={(e) => setResetScope(e.target.value)}>
                  {Object.entries(RESET_SCOPES).map(([k, v]) => (
                    <FormControlLabel
                      key={k}
                      value={k}
                      control={<Radio size="small" />}
                      sx={{ alignItems: 'flex-start', mb: 0.5 }}
                      label={<Box><Typography variant="body2">{v.label}</Typography><Typography variant="caption" color="text.secondary">{v.hint}</Typography></Box>}
                    />
                  ))}
                </RadioGroup>
              </Box>
            )}
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Your note"
              placeholder={PLACEHOLDERS[type] || PLACEHOLDERS.feedback}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              autoFocus
            />
            {contextChips.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  Attached automatically:
                </Typography>
                {contextChips.map((c) => (
                  <Chip key={c.label} label={c.label} size="small" variant="outlined" />
                ))}
              </Box>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}{' '}
                <Link href={feedbackMailto(type, message)}>Email it instead</Link>.
              </Alert>
            )}
            {/* Tim 26 Oct (D03 r3): the mailto link opens whatever the OS default is (Outlook for him); a copy button serves webmail users */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              Prefer email? <Link href={feedbackMailto(type, message)}>{FEEDBACK_EMAIL}</Link>
              <Button
                size="small"
                variant="text"
                sx={{ minWidth: 0, py: 0, textTransform: 'none' }}
                onClick={() => {
                  if (typeof navigator !== 'undefined' && navigator.clipboard) navigator.clipboard.writeText(FEEDBACK_EMAIL).then(() => { setCopiedAddr(true); setTimeout(() => setCopiedAddr(false), 2000); }).catch(() => {});
                }}
              >
                {copiedAddr ? 'Copied' : 'Copy address'}
              </Button>
            </Typography>
            {organization?.id && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Only need your dismissed OE Health Alerts back? That part you can do yourself, right now — it affects only your view.
                </Typography>
                <Button size="small" variant="outlined" disabled={busy || restored != null} onClick={handleRestoreAlerts}>
                  {restored == null ? 'Restore demo alerts' : restored === 0 ? 'Nothing to restore' : `Restored ${restored} alert${restored === 1 ? '' : 's'}`}
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {sentId ? (
          <Button variant="contained" onClick={close}>Done</Button>
        ) : (
          <>
            <Button onClick={close} disabled={busy} color="inherit">Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={busy || (type !== 'sandbox_reset' && !message.trim())}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            >
              {busy ? 'Sending…' : 'Send to the build team'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
